'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';
import { motion } from 'framer-motion';

import { cn } from '@/lib/utils';

interface TabsContextValue {
	activeValue?: string;
	direction: -1 | 0 | 1;
	animationTick: number;
	enableContentAnimation: boolean;
	registerTrigger: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

interface TabsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
	enableContentAnimation?: boolean;
}

const getDirection = (
	fromValue: string | undefined,
	toValue: string | undefined,
	tabOrder: string[]
): -1 | 0 | 1 => {
	if (!fromValue || !toValue || fromValue === toValue) {
		return 0;
	}

	const fromIndex = tabOrder.indexOf(fromValue);
	const toIndex = tabOrder.indexOf(toValue);

	if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
		return 0;
	}

	return toIndex > fromIndex ? 1 : -1;
};

function Tabs({
	className,
	value,
	defaultValue,
	onValueChange,
	enableContentAnimation = true,
	...props
}: TabsProps) {
	const controlledValue = typeof value === 'string' ? value : undefined;
	const [uncontrolledValue, setUncontrolledValue] = React.useState<
		string | undefined
	>(
		typeof defaultValue === 'string'
			? defaultValue
			: controlledValue
	);
	const activeValue = controlledValue ?? uncontrolledValue;
	const previousValueRef = React.useRef<string | undefined>(activeValue);
	const [direction, setDirection] = React.useState<-1 | 0 | 1>(0);
	const [animationTick, setAnimationTick] = React.useState(0);
	const [tabOrder, setTabOrder] = React.useState<string[]>([]);

	const registerTrigger = React.useCallback((triggerValue: string) => {
		setTabOrder(previous =>
			previous.includes(triggerValue)
				? previous
				: [...previous, triggerValue]
		);
	}, []);

	const handleValueChange = React.useCallback(
		(nextValue: string) => {
			const currentValue = controlledValue ?? previousValueRef.current;
			const nextDirection = getDirection(currentValue, nextValue, tabOrder);
			setDirection(nextDirection);
			if (nextDirection !== 0) {
				setAnimationTick(previous => previous + 1);
			}

			previousValueRef.current = nextValue;

			if (controlledValue === undefined) {
				setUncontrolledValue(nextValue);
			}

			onValueChange?.(nextValue);
		},
		[controlledValue, onValueChange, tabOrder]
	);

	React.useEffect(() => {
		if (controlledValue === undefined) {
			return;
		}

		const previousValue = previousValueRef.current;
		const nextDirection = getDirection(
			previousValue,
			controlledValue,
			tabOrder
		);
		setDirection(nextDirection);
		if (nextDirection !== 0) {
			setAnimationTick(previous => previous + 1);
		}
		previousValueRef.current = controlledValue;
	}, [controlledValue, tabOrder]);

	const contextValue = React.useMemo<TabsContextValue>(
		() => ({
			activeValue,
			direction,
			animationTick,
			enableContentAnimation,
			registerTrigger
		}),
		[
			activeValue,
			direction,
			animationTick,
			enableContentAnimation,
			registerTrigger
		]
	);

	return (
		<TabsContext.Provider value={contextValue}>
			<TabsPrimitive.Root
				data-slot="tabs"
				className={cn('flex flex-col', className)}
				value={value}
				defaultValue={defaultValue}
				onValueChange={handleValueChange}
				{...props}
			/>
		</TabsContext.Provider>
	);
}

function TabsList({
	className,
	...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
	return (
		<TabsPrimitive.List
			data-slot="tabs-list"
			className={cn(
				'flex items-center gap-6 w-full',
				className
			)}
			{...props}
		/>
	);
}

interface TabsTriggerProps
	extends React.ComponentProps<typeof TabsPrimitive.Trigger> {
	align?: 'left' | 'right';
}

function TabsTrigger({
	className,
	align = 'left',
	...props
}: TabsTriggerProps) {
	const tabsContext = React.useContext(TabsContext);
	const triggerValue = props.value;
	const triggerRef = React.useRef<HTMLButtonElement | null>(null);
	const [hideIcon, setHideIcon] = React.useState(false);

	const updateIconVisibility = React.useCallback(() => {
		const triggerElement = triggerRef.current;
		if (!triggerElement) {
			return;
		}

		const hasDirectIcon = Array.from(triggerElement.children).some(
			child => child.tagName.toLowerCase() === 'svg'
		);

		if (!hasDirectIcon) {
			setHideIcon(false);
			return;
		}

		// Measure overflow with icon visible; hide icon only when content does not fit.
		const previousHideIconAttr = triggerElement.getAttribute('data-hide-icon');
		triggerElement.setAttribute('data-hide-icon', 'false');
		const isOverflowing = triggerElement.scrollWidth > triggerElement.clientWidth + 1;

		if (previousHideIconAttr == null) {
			triggerElement.removeAttribute('data-hide-icon');
		} else {
			triggerElement.setAttribute('data-hide-icon', previousHideIconAttr);
		}

		setHideIcon(isOverflowing);
	}, []);

	React.useEffect(() => {
		if (typeof triggerValue !== 'string' || !tabsContext) {
			return;
		}

		tabsContext.registerTrigger(triggerValue);
	}, [tabsContext, triggerValue]);

	React.useLayoutEffect(() => {
		const triggerElement = triggerRef.current;
		if (!triggerElement) {
			return;
		}

		let rafId: number | null = null;
		const scheduleMeasurement = () => {
			if (rafId != null) {
				cancelAnimationFrame(rafId);
			}

			rafId = requestAnimationFrame(() => {
				updateIconVisibility();
				rafId = null;
			});
		};

		scheduleMeasurement();

		const resizeObserver =
			typeof ResizeObserver !== 'undefined'
				? new ResizeObserver(scheduleMeasurement)
				: null;
		resizeObserver?.observe(triggerElement);

		return () => {
			if (rafId != null) {
				cancelAnimationFrame(rafId);
			}
			resizeObserver?.disconnect();
		};
	}, [updateIconVisibility, props.children, className, triggerValue]);

	return (
		<TabsPrimitive.Trigger
			data-slot="tabs-trigger"
			ref={triggerRef}
			data-hide-icon={hideIcon ? 'true' : 'false'}
			className={cn(
				"relative inline-flex min-w-0 shrink items-center gap-2 overflow-hidden rounded-lg border border-transparent px-3 py-2 text-sm font-medium text-muted-foreground transition-colors whitespace-nowrap",
				"bg-black/[0.015] dark:bg-white/[0.015]",
				"hover:text-foreground hover:bg-black/[0.03] dark:hover:bg-white/[0.03]",
				"data-[state=active]:border-border data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm",
				"data-[hide-icon=true]:[&>svg]:hidden",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				"disabled:pointer-events-none disabled:opacity-50",
				"[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
				"[&>span]:block [&>span]:min-w-0 [&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:whitespace-nowrap",
				align === 'right' && 'ml-auto',
				className
			)}
			{...props}
		/>
	);
}

function TabsContent({
	className,
	children,
	...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
	const tabsContext = React.useContext(TabsContext);
	const contentValue = props.value;

	React.useEffect(() => {
		if (typeof contentValue !== 'string' || !tabsContext) {
			return;
		}

		tabsContext.registerTrigger(contentValue);
	}, [tabsContext, contentValue]);

	const isActive =
		typeof contentValue === 'string' &&
		contentValue === tabsContext?.activeValue;
	const shouldAnimate =
		Boolean(isActive) &&
		Boolean(tabsContext?.enableContentAnimation) &&
		tabsContext?.direction !== 0;
	const contentKey = isActive
		? `${contentValue}-${tabsContext?.animationTick ?? 0}`
		: String(contentValue ?? '');

	return (
		<TabsPrimitive.Content
			data-slot="tabs-content"
			className={cn('flex-1 outline-none pt-4', className)}
			{...props}
		>
			<motion.div
				key={contentKey}
				initial={
					shouldAnimate
						? { x: (tabsContext?.direction ?? 0) > 0 ? 24 : -24 }
						: { x: 0 }
				}
				animate={{ x: 0 }}
				transition={{ duration: 0.16, ease: 'easeOut' }}
			>
				{children}
			</motion.div>
		</TabsPrimitive.Content>
	);
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
